import { createSelector } from '@reduxjs/toolkit';
import { urls } from '@ff-os/shared-utils/urls';
import { selectAllBorderIds, selectAllBorders } from '@ff-scene-plugins/photo-prints-redux/selectors/borderSelectors';
import { selectAllVariants, selectVariantById } from '@ff-scene-plugins/photo-prints-redux/selectors/variantSelectors';
import { PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import { CompatibleWithRule, VariantOptionWithPriceRequest } from './VariantOption';

const selectBorderIdsForSizeAndPaper = createSelector([
    selectAllVariants,
    (state: PhotoPrintsPdpState, sizeId: string | undefined) => sizeId,
    (state: PhotoPrintsPdpState, sizeId: string | undefined, paperId: string | undefined) => paperId
], (variants, sizeId, paperId) => {
    return variants
        .filter(v => (sizeId === undefined || v.sizeId === sizeId) && (paperId === undefined || v.paperId === paperId))
        .map(v => v.borderId);
});

const compatibleSizeAndPaperIdsByBorderId = createSelector([
    selectAllBorders,
    selectAllVariants
], (borders, variants) => {
    const compatibleSizeIdsByBorderId  = {} as Record<string,CompatibleWithRule[]>;
    for (const border of borders) {
        compatibleSizeIdsByBorderId[border.id] = variants
            .filter(v => v.borderId === border.id)
            .map(v => ({sizeId: v.sizeId, paperId: v.paperId}));
    }
    return compatibleSizeIdsByBorderId ;
});

export const selectDefaultBorderId = createSelector([
    selectAllBorders
], borders => borders.find(b => b.default)?.id ?? '');

const selectSkuByBorderId = createSelector([
    selectAllBorderIds,
    selectAllVariants,
    (state: PhotoPrintsPdpState, sizeId?: string) => sizeId,
    (state: PhotoPrintsPdpState, sizeId?: string, paperId?: string) => paperId
],(borderIds, variants, sizeId, paperId) => {
    const skuBySizeId = {} as Record<string, string| undefined>;

    for(const id of borderIds){
        skuBySizeId[id] = variants.find(item => item.sizeId === sizeId && item.paperId === paperId && item.borderId === id)?.sku;
    }

    return skuBySizeId;
});

export const selectBorderOptions = createSelector([
    selectAllBorders,
    selectBorderIdsForSizeAndPaper,
    compatibleSizeAndPaperIdsByBorderId,
    selectSkuByBorderId
], (allBorders, availableBorderIds, compatibleSizeIdsByBorderId, selectSku) => {
    return allBorders.map(b =>
        toVariantOptions(b, availableBorderIds, compatibleSizeIdsByBorderId, selectSku)
    );
});

export const selectCompatibleBorderOptions = createSelector([
    selectBorderOptions
], borderOptions => borderOptions.filter(b => b.available).length > 0
        ? borderOptions.filter(b => b.available)
        : borderOptions
);

const selectSizeIdByVariantId = createSelector([
    selectVariantById
], variant => variant!.sizeId);

const selectBorderOptionsByVariantId = createSelector([
    state => state,
    selectSizeIdByVariantId
], selectBorderOptions);

export const selectActiveBorderOptionForVariantId = createSelector([
    selectVariantById,
    selectBorderOptionsByVariantId
], (variant, borders) => borders.find(b => b.value === variant!.borderId)!);

export const selectHasFotofunBorder = createSelector([
    selectAllBorders
], borders => borders.some(border => border.id === 'fotofun-border'));

const toVariantOptions = (
    border: PhotoPrintsJson.Border,
    availableBorderIds: string[],
    compatibleSizeIdsByBorderId: Record<string, CompatibleWithRule[]>,
    selectSkuByBorderId: Record<string, string | undefined>
): VariantOptionWithPriceRequest => ({
    value: border.id,
    name: { key: border.name },
    iconUrl: urls.publicAssetUrl(border.iconAssetId),
    compatibleWith: compatibleSizeIdsByBorderId[border.id],
    available: availableBorderIds.includes(border.id),
    price: undefined,
    priceRequest: {
        sku: selectSkuByBorderId[border.id] ?? ''
    },
    default: border.default
});